import { IonReorder, IonReorderGroup, IonRouterLink, ItemReorderEventDetail } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SwiperSlide } from 'swiper/react';

import ExpandableSection from '@components/animations/ExpandableSection';
import TertiaryButton from '@components/buttons/TertiaryButton';
import GoalCard from '@components/cards/GoalCard';
import ArchiveSegment from '@components/carePlan/ArchiveSegment';
import styles from '@components/carePlan/CarePlan.module.css';
import CardSlides from '@components/layout/CardSlides';
import Section from '@components/layout/Section';
import Modal from '@components/Modal';
import Icon from '@components/text/Icon';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Heading2 from '@components/typography/Heading2';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithPlanQuery,
  GoalsReorderDocument,
  GoalsReorderMutation,
  GoalsReorderMutationVariables
} from '@typing/Generated';
import { imageUrl } from '@utils/imageUtils';
import { moveArrayByIndex, sortByPosition } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Journey = GetJourneyWithPlanQuery['journey'];

type Props = {
  journey: Journey;
};

type InitialCardProps = {
  journeyId: string;
};

const InitialCard = ({ journeyId }: InitialCardProps) => {
  const myExpert = useMyExpert();

  return (
    <IonRouterLink
      className={styles.goalBrowseContainer}
      routerLink={journeyRoute({
        journeyId,
        linkAfterJourney: '/care-plan/goal-templates',
        myExpert
      })}
    >
      <div className={styles.goalBrowse}>
        <div>
          <img className={styles.browseGoalImg} src={imageUrl('planChecklist')} />
        </div>
        <div className={styles.setGoal}>
          <Icon ariaLabelKey="accessibility.ariaLabels.add" className={styles.browseGoalIcon} icon="add" />
          <div className={styles.noWrap}>
            <Heading2 color="shadesWhite">
              <FormattedMessage id="pages.carePlan.base.goals.set" />
            </Heading2>
          </div>
        </div>
      </div>
    </IonRouterLink>
  );
};

const CarePlanGoals = ({ journey }: Props) => {
  const expandedOpener = useOpener();

  const intl = useIntl();

  const reorderOpener = useOpener();

  const [archived, setArchived] = useState(false);

  const goals = useMemo(() => {
    if (journey.carePlanPublishedAt) {
      return sortByPosition(journey.goals)
        .filter(goal => !goal.draft)
        .filter(goal => goal.archived === archived);
    } else {
      return sortByPosition(journey.goals).filter(goal => goal.author.__typename === 'Client');
    }
  }, [archived, journey.carePlanPublishedAt, journey.goals]);

  const [goalsReorder] = useMutationWithValidation<GoalsReorderMutation, GoalsReorderMutationVariables>(
    GoalsReorderDocument,
    'goal'
  );

  const reorderGoals = useCallback(
    (event: CustomEvent<ItemReorderEventDetail>) => {
      const newGoalsOrder = moveArrayByIndex(event, goals).map(goal => goal.id);

      if (archived) {
        sortByPosition(journey.goals.filter(goal => goal.archived !== archived))
          .reverse()
          .forEach(g => newGoalsOrder.unshift(g.id));
      } else {
        sortByPosition(journey.goals.filter(goal => goal.archived !== archived))
          .reverse()
          .forEach(g => newGoalsOrder.push(g.id));
      }

      goalsReorder({
        goalIds: newGoalsOrder,
        journeyId: journey.id
      }).then(() => {
        event.detail.complete();
      });
    },
    [goals, archived, goalsReorder, journey.id, journey.goals]
  );

  return (
    <Section
      color="primary700"
      descriptionKey="pages.carePlan.base.goals.description"
      icon="listCheck"
      titleKey="pages.carePlan.base.goals.title"
    >
      <div className={styles.flex}>
        <ArchiveSegment
          count={journey.goals.filter(goal => !goal.draft).filter(goal => goal.archived !== archived).length}
          setValue={setArchived}
          value={archived}
        />
      </div>

      <div className={styles.showMobile}>
        <CardSlides itemCount={goals.length}>
          {!archived && goals.length === 0 && (
            <SwiperSlide>
              <InitialCard journeyId={journey.id} />
            </SwiperSlide>
          )}
          {goals.map(goal => (
            <SwiperSlide key={goal.id}>
              <GoalCard goal={goal} journey={journey} />
            </SwiperSlide>
          ))}
          {goals.length < 2 && !archived && (
            <SwiperSlide>
              <div className={styles.emptyGoal}>
                <Heading2 color="neutral500">
                  <FormattedMessage id="pages.carePlan.base.goals.empty" />
                </Heading2>
              </div>
            </SwiperSlide>
          )}
        </CardSlides>
      </div>

      <div className={styles.hideMobile}>
        <div className={styles.topGoals}>
          {!archived && <InitialCard journeyId={journey.id} />}

          {goals.slice(0, 5).map(goal => (
            <div className={styles.goal} key={goal.id}>
              <GoalCard goal={goal} journey={journey} />
            </div>
          ))}
          {goals.length < 2 && !archived && (
            <div className={styles.emptyGoal}>
              <Heading2 color="neutral500">
                <FormattedMessage id="pages.carePlan.base.goals.empty" />
              </Heading2>
            </div>
          )}
        </div>
        <div>
          <ExpandableSection isOpen={expandedOpener.isOpen} key="goals-rest">
            <div className={styles.topGoals}>
              {goals.slice(5).map(goal => (
                <div className={styles.goal} key={goal.id}>
                  <GoalCard goal={goal} journey={journey} />
                </div>
              ))}
            </div>
          </ExpandableSection>
        </div>

        {goals.length > 5 && (
          <div className="align-center">
            <TertiaryButton
              icon={expandedOpener.isOpen ? 'chevronUp' : 'chevronDown'}
              iconPosition="end"
              onClick={expandedOpener.toggle}
              textKey="pages.carePlan.base.goals.showMore"
            />
          </div>
        )}
      </div>
      <Modal opener={reorderOpener} title={intl.formatMessage({ id: 'pages.carePlan.base.goals.reorder' })}>
        {() => (
          <div>
            <ArchiveSegment
              count={journey.goals.filter(goal => !goal.draft).filter(goal => goal.archived !== archived).length}
              setValue={setArchived}
              value={archived}
            />

            <div>
              <IonReorderGroup disabled={false} onIonItemReorder={reorderGoals}>
                {goals.map(goal => (
                  <div className={styles.reorderGoal} key={goal.id}>
                    <BodyCopyBold>{goal.title}</BodyCopyBold>
                    <IonReorder slot="start" />
                  </div>
                ))}
              </IonReorderGroup>
            </div>
          </div>
        )}
      </Modal>
    </Section>
  );
};

export default CarePlanGoals;
